
























import { Component, Prop, Vue } from 'vue-property-decorator'
import { KVConfiguration, KVEventBus } from '@/embed/widget/App.vue'
import { WidgetFlowType, WidgetFont, WidgetPopupAnimation, WidgetPopupContent, WidgetPopupPosition, WidgetPopupType, WidgetType } from '@/api/types'
import IconCross from '@/components/icons/IconCross.vue'
import { WidgetAction } from '@/embed/widget/actions'
import SylvanaCircle from '@/components/avatar/SylvanaCircle.vue'
import BrunetteCircle from '@/components/avatar/BrunetteCircle.vue'
import BlondCircle from '@/components/avatar/BlondCircle.vue'

export interface PopupConfiguration {
    flow: WidgetFlowType
    style: WidgetPopupType
    position: WidgetPopupPosition
    animation: WidgetPopupAnimation
    animationPopupTime: number
    backgroundColor: string
    textColor: string
    buttonBackgroundColor: string
    buttonTextColor: string
    font: WidgetFont
    contents: WidgetPopupContent[]
}

@Component({
    components: {IconCross, BlondCircle, BrunetteCircle, SylvanaCircle},
})
export default class Popup extends Vue {
    @Prop({required: true}) config!: KVConfiguration
    firstShowTimeout = null
    showContent: boolean = false
    showPopup: boolean = false

    mounted() {
        this.showPopup = !this.hasBeenClosedBefore
        this.animatePopup()

        KVEventBus.$on(WidgetAction.WIDGET_CLOSE, this.hide)
        KVEventBus.$on(WidgetAction.WIDGET_OPEN, this.show)
    }

    hide() {
        this.showContent = false
    }

    show() {
        this.showContent = true
    }

    animatePopup() {
        if (this.config && this.config.popup.animation === WidgetPopupAnimation.NONE) {
            return
        }

        this.firstShowTimeout = setTimeout(() => {
            this.firstShowTimeout = null
        }, 1000 * this.config.popup.animationPopupTime)
    }

    togglePopup() {
        this.showContent = !this.showContent
    }

    closePopup() {
        this.showPopup = false
        const expire = new Date()
        expire.setTime((new Date()).getTime() + 3600000 * 24 * 14)
        document.cookie = 'kv-popup-closed=true; SameSite = none; Secure; expires=' + expire.toUTCString()
    }

    get showComponent(): boolean {
        return this.config ? this.config.type === WidgetType.POPUP && this.config.flows.includes(this.config.popup.flow) : false
    }

    get componentClasses(): object {
        if (!this.config) {
            return {}
        }

        return {
            'kv-popup': true,
            'kv-popup--left': this.config.popup.position === WidgetPopupPosition.LEFT,
            'kv-popup--right': this.config.popup.position === WidgetPopupPosition.RIGHT,
            'kv-popup--rectangle': this.config.popup.style === WidgetPopupType.RECTANGLE,
            'kv-popup--circular': this.config.popup.style === WidgetPopupType.CIRCULAR,
            'kv-popup--card': this.config.popup.style === WidgetPopupType.CARD,
            'kv-popup--show-content': this.firstShowTimeout !== null || this.showContent,
            'kv-popup--hide': !this.showPopup,
        }
    }

    get content(): WidgetPopupContent {
        return this.config.popup.contents.find(item => item.flow === this.config.popup.flow)
    }

    get cssVars(): object {
        if (!this.config) {
            return {}
        }

        return {
            '--background-color': this.config.popup.backgroundColor,
            '--text-color': this.config.popup.textColor,
            '--button-background-color': this.config.popup.buttonBackgroundColor,
            '--button-text-color': this.config.popup.buttonTextColor,
            '--font': this.config.popup.font,
        }
    }

    get hasBeenClosedBefore(): boolean {
        return document.cookie.split(';').some(item => {
            return item.trim().indexOf('kv-popup-closed=') === 0
        })
    }

    get widgetLink(): string {
        return process.env.VUE_APP_KEYVUE_WIDGET_BASEURL + Object.values(WidgetAction).find(value => value as string === this.config.popup.flow)
    }

    get isCardStyle(): boolean {
        return this.config.popup.style === WidgetPopupType.CARD
    }

    get isToggleHeader(): boolean {
        return this.config.popup.style === WidgetPopupType.CIRCULAR
    }

    get avatarType(): string {
        const types: string[] = ['brunette-circle', 'blond-circle', 'sylvana-circle']

        return types[this.config.avatarStyle]
    }
}
